import type { LucideIcon } from 'lucide-react'
import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { Sparkline } from './Sparkline'
import { BigNumber, Eyebrow } from '@/components/hawk-primitives'

export interface Metric {
  label: string
  value: string
  delta?: { value: string; direction: 'up' | 'down'; positive: boolean }
  spark?: number[]
}

interface MetricCardProps {
  title: string
  icon?: LucideIcon
  metrics: Metric[]
}

export function MetricCard({ title, icon: Icon, metrics }: MetricCardProps) {
  return (
    <div className="relative border border-border bg-card rounded-md overflow-hidden">
      <div
        className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"
        aria-hidden
      />
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-background/30">
        {Icon && (
          <Icon
            className="size-3.5 text-muted-foreground"
            strokeWidth={1.5}
          />
        )}
        <Eyebrow>{title}</Eyebrow>
      </div>

      <div className="grid grid-cols-2 divide-x divide-border">
        {metrics.map((m) => (
          <div key={m.label} className="min-w-0 px-5 py-5 space-y-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground truncate">
              {m.label}
            </div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <BigNumber value={m.value} className="text-[34px]" />
              {m.delta && (
                <div
                  className={`flex items-center gap-0.5 text-xs font-mono tabular-nums ${
                    m.delta.positive
                      ? 'text-[var(--color-signal)]'
                      : 'text-destructive'
                  }`}
                >
                  {m.delta.direction === 'up' ? (
                    <ArrowUpRight className="size-3" />
                  ) : (
                    <ArrowDownRight className="size-3" />
                  )}
                  {m.delta.value}
                </div>
              )}
            </div>
            {m.spark && m.spark.length > 0 && (
              <div className="pt-1">
                <Sparkline
                  data={m.spark}
                  tone={m.delta?.positive === false ? 'negative' : 'primary'}
                  height={26}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
